/**
 * MeslekYonetimPage.jsx — Meslekler ve Belge Kuralları (inceleme/admin tarafı)
 * Route: yok — #385'ten beri Kişi Kuralları sayfasının "Meslek Kuralları" sekmesi (/Management/Kisi/Kurallar?tab=meslek,
 * pages/Management/Kisi/KisiKurallariPage.jsx); eski /Management/Kisi/Meslekler adresi oraya yönlenir.
 *
 * "Bu işi yapan bir işçi hangi belgeleri getirmek zorunda?" sorusunun tek yönetim yeri.
 * Bir meslek kuralı, o işi yapan herkes için KİŞİSEL BELGE KÜMESİNİN TAMAMINI söyler ve
 * varsayılan dörtlünün (SGK / İSG Eğitimi / Sağlık Onayı / Mesleki Eğitim) yerine geçer —
 * kümeyi GENİŞLETEBİLDİĞİ gibi DARALTABİLİR de (nakliyeci araçtan inmediği için yalnız
 * SGK + Mesleki Eğitim). Bu yüzden mesleği seçmek, işçinin ne üretmek zorunda olduğunu
 * seçmektir ve kural yazma uçları SAKA personeline kilitli.
 *
 * Servis: services/kisiAdminService.js
 * Rehber: docs/guides/FE-PersonnelController-kimlik-meslek-guide.md §5
 *
 * Sayfanın kasten görünür kıldığı dört tuzak:
 *  1. `kaynak` HER YERDE gösteriliyor — çünkü boş liste iki ZIT anlama gelir:
 *     VARSAYILAN + [] = "kimse karar vermedi, varsayılan dörtlü geçerli",
 *     GLOBAL/FUAR + [] = "bu meslek gerçekten hiçbir belge istemiyor".
 *  2. Fuar boyutu meslek boyutunu YENER: "fuar 298'de herkes X" yazmak, o fuardaki
 *     global nakliyeci kuralını yutar. Editörde bu bir uyarı olarak duruyor.
 *  3. `[]` kaydetmek ile kuralı SİLMEK farklı şeyler: ilki "hiçbir şey istemiyorum",
 *     ikincisi "bir fikrim yok, üst kademe karar versin". İki ayrı buton.
 *  4. Mesleği KALDIRMAK üçüncü bir şey (#148): katalogdan çıkarır, ama mesleği taşıyan kişiler
 *     onu korur ve kuralı geçerli kalır. Satırdaki "Mesleği Kaldır" (Archive) ile editördeki
 *     "Kuralı Sil" (üst kademeye bırak) bilerek AYRI yerlerde ve ayrı simgelerle duruyor —
 *     asla yan yana değiller, hiçbiri çöp kutusu değil. Kaldırılanlar silinmez, aşağıdaki
 *     "Kaldırılan Meslekler" bölümünde Geri Al ile bekler; kuralları orada da düzenlenebilir.
 *
 * Liste `kaldirilanlar=true` ile TEK çağrıda okunur ve `kaldirildi` bayrağına göre bölünür.
 * Kaldırma küreseldir (katalog), kapsam seçicisinden bağımsızdır: seçici yalnızca hangi kuralın
 * ve hangi kişi sayısının gösterildiğini belirler.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Spinner, Alert, Input, Label,
  Modal, ModalHeader, ModalBody, ModalFooter, Table,
} from "reactstrap";
import {
  Archive, ArchiveRestore, Briefcase, CornerLeftUp, Plus, Save, Users, Info, AlertTriangle, X,
} from "lucide-react";
import { PageFrame, PageBody } from "../../../Components/Common/EmbeddablePage";
import MainContext from "../../../Components/Contexts/MainContext";
import {
  getMeslekList, getMeslekBelgeKural, saveMeslekBelgeKural, deleteMeslekBelgeKural,
  createMeslek, kaldirMeslek, geriAlMeslek, getKisiFileTypes, isStaffRefusal,
  KURAL_KAYNAK, KVKK_FILE_TYPE_ID, DEFAULT_FUAR_ID, UYRUK_DUZEYI,
} from "../../../services/kisiAdminService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const GLOBAL_SCOPE = 0;

/** `meslekId: 0` bir MESLEK DEĞİL, bir KAPSAM — "bu fuar/sistem herkesten ne istiyor". */
const TUM_MESLEKLER_ID = 0;

/**
 * #430: bir satırın seçili UYRUK DÜZEYİNDE gösterdiği kural: "hepsi" kuralı (belgeKurali) ya da düzeyin KENDİ kuralı (yoksa null, geri dönüş gösterilmez).
 * Liste ucu her mesleğe `yerliKurali` / `yabanciKurali` koyar; bir kişi önce kendi düzeyindeki kuralla, ancak o yoksa "hepsi" kuralıyla yargılanır.
 */
function seviyeKurali(m, uyruk) {
  if (uyruk === UYRUK_DUZEYI.YERLI) return m?.yerliKurali ?? null;
  if (uyruk === UYRUK_DUZEYI.YABANCI) return m?.yabanciKurali ?? null;
  return m?.belgeKurali ?? null;
}

function KaynakBadge({ kaynak }) {
  const meta = KURAL_KAYNAK[kaynak] ?? { label: kaynak ?? "—", color: "secondary" };
  return <Badge color={meta.color} className="fw-normal">{meta.label}</Badge>;
}

function BelgeChips({ kural, fileTypes, uyruk = 0 }) {
  const { t } = useTranslation("yonetim");
  // #430: a nationality level with no rule of its own says so (the "any" rule or the seeded foreign one answers for it)
  if (!kural) {
    return uyruk === 0
      ? <span className="text-muted small">—</span>
      : <span className="text-muted small fst-italic" data-testid="uyruk-kural-yok">{t("meslekYonetim.uyrukKuralYok")}</span>;
  }
  const ids = kural.fileTypeIds || [];

  // Boş liste + VARSAYILAN ≠ boş liste + yazılmış kural. Aynı görünüp zıt anlama gelen
  // tek yer burası, bu yüzden metin de farklı.
  if (ids.length === 0) {
    return kural.kaynak === "VARSAYILAN"
      ? <span className="text-muted small fst-italic">{t("meslekYonetim.varsayilanDortluSgkIsgSaglik")}</span>
      : <span className="text-danger small fw-semibold">{t("meslekYonetim.hicbirKisiselBelgeIstenmiyor")}</span>;
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {ids.map((id) => {
        const fromRule = (kural.belgeler || []).find((b) => b.fileTypeId === id);
        const fromCatalog = fileTypes.find((f) => f.fileTypeId === id);
        return (
          <Badge key={id} color="light" className="text-body border fw-normal">
            {fromRule?.ad ?? fromCatalog?.fileTypeName ?? t("meslekYonetim.fileType", { id })}
          </Badge>
        );
      })}
    </div>
  );
}

function formatTarih(deger) {
  if (!deger) return "—";
  const d = new Date(deger);
  return Number.isNaN(d.getTime()) ? String(deger) : d.toLocaleDateString(currentLocaleTag());
}

/** Kaldırma sonrası bilgi satırı — yanıttaki sayılar, mesleğin kimlerde kaldığını söyler. */
function kaldirmaOzeti(ad, sonuc) {
  const parcalar = [];
  if (sonuc?.kisiSayisi) parcalar.push(i18n.t("yonetim:meslekYonetim.kisiIseAlimda", { kisiSayisi: sonuc.kisiSayisi }));
  if (sonuc?.kartMeslegiKisiSayisi) parcalar.push(i18n.t("yonetim:meslekYonetim.kisiKartinda", { kartMeslegiKisiSayisi: sonuc.kartMeslegiKisiSayisi }));
  return parcalar.length
    ? i18n.t("yonetim:meslekYonetim.meslegiKaldirildiOnuKorur", { ad, v: parcalar.join(", ") })
    : i18n.t("yonetim:meslekYonetim.meslegiKaldirildi", { ad });
}

/**
 * Sunumdaki ve kaldırılmış meslekleri aynı sütunlarla çizer; fark yalnızca sondaki iki sütun.
 * ⚠️ İki satır eylemi KASTEN farklı: "Mesleği Kaldır" mesleği katalogdan çıkarır (Archive),
 * "Kuralı Düzenle" yalnızca belge kuralına gider. Kuralı SİLMEK (üst kademeye bırakmak) bu
 * tabloda hiç yok — editörün içinde, ayrı bir simgeyle duruyor.
 */
function MeslekTablosu({
  rows, fileTypes, kaldirilmis = false, busyId, bosMetin, onKuralDuzenle, onKaldir, onGeriAl, uyruk = 0,
}) {
  const { t } = useTranslation("yonetim");
  return (
    <div className="table-responsive">
      {/* `table-nowrap` YOK: meslek adları çok uzun ("TEHLİKELİ VE ÇOK TEHLİKELİ
          İŞLERDE …") ve nowrap ile sağdaki Kural Kaynağı/İstenen Belgeler sütunları
          ekran dışına kayıyordu. Adın sarması, kuralın görünmesinden daha ucuz. */}
      <Table className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: 56 }}>{t("meslekYonetim.id")}</th>
            <th style={{ minWidth: 170 }}>{t("meslekYonetim.meslek")}</th>
            <th style={{ width: 64 }}>{t("meslekYonetim.kisi")}</th>
            <th style={{ width: 165 }}>{t("meslekYonetim.kaynak")}</th>
            <th style={{ minWidth: 210 }}>{t("meslekYonetim.istenenBelgeler")}</th>
            {kaldirilmis && <th style={{ width: 110 }}>{t("meslekYonetim.kaldirildi")}</th>}
            <th style={{ width: 250 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={kaldirilmis ? 7 : 6} className="text-center text-muted py-4">{bosMetin}</td></tr>
          )}
          {rows.map((m) => (
            <tr key={m.meslekId}>
              <td className="text-muted">{m.meslekId}</td>
              <td>
                <span className={`d-inline-flex align-items-center gap-2 ${kaldirilmis ? "text-muted" : ""}`}>
                  <Briefcase size={14} className="text-muted" />
                  <span className="fw-semibold">{m.ad}</span>
                </span>
                {m.aciklama && <div className="small text-muted">{m.aciklama}</div>}
              </td>
              <td>{m.kisiSayisi}</td>
              {/* WAS (until #430): kaynak={m.belgeKurali?.kaynak} / kural={m.belgeKurali}; since #430 the rule of the chosen nationality level */}
              <td style={{ whiteSpace: "normal" }}><KaynakBadge kaynak={seviyeKurali(m, uyruk)?.kaynak} /></td>
              <td style={{ whiteSpace: "normal" }}>
                <BelgeChips kural={seviyeKurali(m, uyruk)} fileTypes={fileTypes} uyruk={uyruk} />
              </td>
              {kaldirilmis && <td className="small text-muted">{formatTarih(m.kaldirilmaTarihi)}</td>}
              <td>
                <div className="d-flex justify-content-end flex-wrap gap-1">
                  {kaldirilmis && (
                    <Button color="success" outline size="sm" className="text-nowrap" disabled={busyId === m.meslekId} onClick={() => onGeriAl(m)}>
                      {busyId === m.meslekId
                        ? <Spinner size="sm" />
                        : <><ArchiveRestore size={13} className="me-1" />{t("meslekYonetim.geriAl")}</>}
                    </Button>
                  )}
                  <Button color="light" size="sm" className="text-nowrap" onClick={() => onKuralDuzenle(m)}>{t("meslekYonetim.kuraliDuzenle")}</Button>
                  {!kaldirilmis && (
                    <Button color="danger" outline size="sm" className="text-nowrap" onClick={() => onKaldir(m)}>
                      <Archive size={13} className="me-1" />{t("meslekYonetim.meslegiKaldir")}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default function MeslekYonetimPage({ embedded = false }) {
  const { t: tx } = useTranslation("yonetim");
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedFuarId } = useContext(MainContext);

  const fuarId = Number(searchParams.get("fuarId")) || Number(selectedFuarId) || DEFAULT_FUAR_ID;
  // Kapsam: kuralı TÜM FUARLAR için mi yoksa yalnız bu fuar için mi okuyup yazıyoruz.
  const [scopeFuarId, setScopeFuarId] = useState(fuarId);
  // #430: the nationality LEVEL the page lists and edits (0 any person, 1 a T.C. person, 2 a foreign person), kept in the address next to the scope
  const uyruk = [0, 1, 2].includes(Number(searchParams.get("uyruk"))) ? Number(searchParams.get("uyruk")) : 0;

  const [mesleks, setMesleks] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [editing, setEditing] = useState(null);   // { meslekId, ad, kaldirildi }
  const [createOpen, setCreateOpen] = useState(false);
  const [kaldirHedef, setKaldirHedef] = useState(null); // onay bekleyen meslek
  const [geriAlinanId, setGeriAlinanId] = useState(null);

  // Gömülüyken başlığı Kişi Kuralları sayfası koyar (#385).
  useEffect(() => { if (!embedded) document.title = i18n.t("yonetim:meslekYonetim.meslekVeBelgeKurallariFds"); }, [embedded]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [list, types] = await Promise.all([
          getMeslekList(scopeFuarId, { kaldirilanlar: true }),
          getKisiFileTypes(),
        ]);
        if (!ignore) { setMesleks(list); setFileTypes(types); }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [scopeFuarId, reloadToken]);

  function showInfo(msg) {
    setInfoMsg(msg);
    setTimeout(() => setInfoMsg(null), 4000);
  }

  function changeScope(next) {
    setScopeFuarId(next);
    const params = new URLSearchParams(searchParams);
    params.set("fuarId", String(next || fuarId));
    setSearchParams(params, { replace: true });
  }

  function changeUyruk(next) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("uyruk", String(next)); else params.delete("uyruk");
    setSearchParams(params, { replace: true });
  }

  const varsayilanSatir = useMemo(
    () => mesleks.find((m) => m.meslekId === TUM_MESLEKLER_ID) ?? null,
    [mesleks],
  );
  const aktifMesleks = useMemo(
    () => mesleks.filter((m) => m.meslekId !== TUM_MESLEKLER_ID && !m.kaldirildi),
    [mesleks],
  );
  const kaldirilmisMesleks = useMemo(() => mesleks.filter((m) => m.kaldirildi), [mesleks]);

  async function handleGeriAl(m) {
    setGeriAlinanId(m.meslekId);
    setError(null);
    try {
      await geriAlMeslek(m.meslekId);
      showInfo(tx("meslekYonetim.meslegiGeriAlindi", { ad: m.ad }));
      setReloadToken((t) => t + 1);
    } catch (e) {
      setError(staffAwareMessage(e));
    } finally {
      setGeriAlinanId(null);
    }
  }

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("meslekYonetim.meslekVeBelgeKurallari")} pageTitle={tx("meslekYonetim.kisi")}>

        <Card className="mb-3">
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col md="auto">
                <Label className="small text-muted mb-1">{tx("meslekYonetim.kapsam")}</Label>
                <Input
                  type="select" bsSize="sm" style={{ minWidth: 260 }}
                  value={String(scopeFuarId)}
                  onChange={(e) => changeScope(Number(e.target.value))}
                >
                  <option value={String(GLOBAL_SCOPE)}>{tx("meslekYonetim.tumFuarlarGenelKural")}</option>
                  <option value={String(fuarId)}>{tx("meslekYonetim.yalnizcaFuar")}{" "}{fuarId}</option>
                </Input>
              </Col>
              <Col md="auto">
                <Label className="small text-muted mb-1">{tx("meslekYonetim.uyrukDuzeyi")}</Label>
                <Input
                  type="select" bsSize="sm" style={{ minWidth: 220 }} value={String(uyruk)}
                  onChange={(e) => changeUyruk(Number(e.target.value))} data-testid="uyruk-duzeyi"
                >
                  <option value="0">{tx("meslekYonetim.uyrukHepsi")}</option>
                  <option value="1">{tx("meslekYonetim.uyrukYerli")}</option>
                  <option value="2">{tx("meslekYonetim.uyrukYabanci")}</option>
                </Input>
              </Col>
              <Col>
                <div className="small text-muted">
                  {tx("meslekYonetim.secilenKapsamAsagidakiHerSatirda")}{" "}<strong>{tx("meslekYonetim.hangiKuralinYururlukteOldugunu")}</strong>{" "}{tx("meslekYonetim.belirlerKaynakRozetiKuralinHangi")}
                </div>
              </Col>
              <Col md="auto">
                <Button color="success" size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus size={14} className="me-1" />{tx("meslekYonetim.yeniMeslek")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {error && <Alert fade={false} color="danger">{error}</Alert>}
        {infoMsg && <Alert fade={false} color="success">{infoMsg}</Alert>}

        {/* #430: what the chosen nationality level means, and when it counts at all */}
        <Alert fade={false} color="info" className="small py-2" data-testid="uyruk-aciklama">
          {uyruk === UYRUK_DUZEYI.YERLI && <div>{tx("meslekYonetim.uyrukAciklamaYerli")}</div>}
          {uyruk === UYRUK_DUZEYI.YABANCI && <div>{tx("meslekYonetim.uyrukAciklamaYabanci")}</div>}
          <div className={uyruk === UYRUK_DUZEYI.HEPSI ? "" : "mt-1"}>{tx("meslekYonetim.uyrukAnahtarNotu")}</div>
        </Alert>

        <Alert fade={false} color="warning" className="d-flex gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
          <div className="small">
            <strong>{tx("meslekYonetim.fuarBoyutuMeslekBoyutunuYener")}</strong>{" "}{tx("meslekYonetim.birFuaraHerkesSunuGetirsin")}{" "}<em>{tx("meslekYonetim.tumFuarlar")}</em>{" "}{tx("meslekYonetim.kapsamindakiMeslekKurallariniYutarBir")}{" "}<em>{tx("meslekYonetim.oFuarIcinDe")}</em>
            {tx("meslekYonetim.yazmakGerekir")}
          </div>
        </Alert>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && (
          <>
            {varsayilanSatir && (
              <Card className="mb-3 border border-primary border-opacity-25">
                <CardHeader className="bg-transparent d-flex align-items-center gap-2">
                  <Users size={16} className="text-primary" />
                  <span className="fw-semibold">{varsayilanSatir.ad}</span>
                  <Badge color="primary" className="ms-1 fw-normal">
                    {tx("meslekYonetim.kisininMeslegiKayitliDegil", { kisiSayisi: varsayilanSatir.kisiSayisi })}
                  </Badge>
                  <Button
                    color="primary" size="sm" outline className="ms-auto"
                    onClick={() => setEditing({ meslekId: TUM_MESLEKLER_ID, ad: varsayilanSatir.ad })}
                  >
                    {tx("meslekYonetim.kuraliDuzenle")}
                  </Button>
                </CardHeader>
                <CardBody className="pt-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* WAS (until #430): the "any" rule only; now the rule of the chosen nationality level */}
                    <KaynakBadge kaynak={seviyeKurali(varsayilanSatir, uyruk)?.kaynak} />
                    <BelgeChips kural={seviyeKurali(varsayilanSatir, uyruk)} fileTypes={fileTypes} uyruk={uyruk} />
                  </div>
                  <div className="small text-muted mt-2">
                    {tx("meslekYonetim.buSatirBirMeslekDegil")}{" "}<strong>{tx("meslekYonetim.kapsamdir")}</strong>{tx("meslekYonetim.meslegiKayitliOlmayanHerkesBu")}
                  </div>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardBody className="p-0">
                <MeslekTablosu
                  rows={aktifMesleks}
                  uyruk={uyruk}
                  fileTypes={fileTypes}
                  bosMetin={tx("meslekYonetim.meslekKaydiBulunamadi")}
                  onKuralDuzenle={(m) => setEditing({ meslekId: m.meslekId, ad: m.ad })}
                  onKaldir={setKaldirHedef}
                />
              </CardBody>
            </Card>

            <Card className="mt-3">
              <CardHeader className="bg-transparent d-flex align-items-center gap-2">
                <Archive size={16} className="text-muted" />
                <span className="fw-semibold">{tx("meslekYonetim.kaldirilanMeslekler")}</span>
                <Badge color="light" className="text-body border fw-normal">{kaldirilmisMesleks.length}</Badge>
              </CardHeader>
              <CardBody className="p-0">
                <MeslekTablosu
                  kaldirilmis
                  rows={kaldirilmisMesleks}
                  uyruk={uyruk}
                  fileTypes={fileTypes}
                  busyId={geriAlinanId}
                  bosMetin={tx("meslekYonetim.kaldirilmisMeslekYok")}
                  onKuralDuzenle={(m) => setEditing({ meslekId: m.meslekId, ad: m.ad, kaldirildi: true })}
                  onGeriAl={handleGeriAl}
                />
              </CardBody>
            </Card>
          </>
        )}
      </PageBody>

      {kaldirHedef && (
        <MeslekKaldirModal
          key={kaldirHedef.meslekId}
          meslek={kaldirHedef}
          onClose={() => setKaldirHedef(null)}
          onRemoved={(msg) => { setKaldirHedef(null); showInfo(msg); setReloadToken((t) => t + 1); }}
        />
      )}

      {editing && (
        <KuralEditorModal
          key={`${editing.meslekId}-${scopeFuarId}-${uyruk}`}
          uyruk={uyruk}
          meslekId={editing.meslekId}
          meslekAdi={editing.ad}
          kaldirildi={Boolean(editing.kaldirildi)}
          fuarId={fuarId}
          scopeFuarId={scopeFuarId}
          fileTypes={fileTypes}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); showInfo(msg); setReloadToken((t) => t + 1); }}
        />
      )}

      {createOpen && (
        <YeniMeslekModal
          key="create"
          onClose={() => setCreateOpen(false)}
          onCreated={(msg) => { setCreateOpen(false); showInfo(msg); setReloadToken((t) => t + 1); }}
        />
      )}
    </PageFrame>
  );
}

/**
 * "Mesleği Kaldır" onayı. Silme değil — metin bunu ve neyin değişmediğini söylüyor: kaldırma
 * küreseldir (kapsam seçicisinden bağımsız), kişiler mesleği korur, belge kuralı geçerli kalır.
 * Sayı bilerek yok: listedeki `kisiSayisi` seçili kapsama göre değişir ve eksik gösterirdi;
 * gerçek sayılar kaldırma yanıtından gelir ve başarı mesajında yer alır.
 */
function MeslekKaldirModal({ meslek, onClose, onRemoved }) {
  const { t } = useTranslation("yonetim");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleKaldir() {
    setSubmitting(true);
    setError(null);
    try {
      const sonuc = await kaldirMeslek(meslek.meslekId);
      onRemoved(kaldirmaOzeti(meslek.ad, sonuc));
    } catch (e) {
      setError(staffAwareMessage(e));
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen toggle={submitting ? undefined : onClose} centered>
      <ModalHeader toggle={submitting ? undefined : onClose}>{t("meslekYonetim.meslegiKaldir")}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}
        <p className="mb-2">
          <strong>{meslek.ad}</strong>{" "}{t("meslekYonetim.meslegiTumFuarlarIcinKatalogdan")}
        </p>
        <p className="mb-0 small text-muted">
          {t("meslekYonetim.meslegiTasiyanKisilerOnuKorur")}{" "}<em>{t("meslekYonetim.geriAl")}</em>{" "}{t("meslekYonetim.ileDoner")}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={submitting}>{t("meslekYonetim.vazgec")}</Button>
        <Button color="danger" onClick={handleKaldir} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : <><Archive size={14} className="me-1" />{t("meslekYonetim.meslegiKaldir")}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Kural editörü. Üç kademeyi birden okur (`etkin` / `fuarKurali` / `globalKural`) ve
 * hangi kademeye yazacağını kullanıcıya AÇIKÇA sorar — yanlış kapsama yazmak bu
 * sistemdeki en pahalı hata (bir fuar kuralı, o fuardaki tüm meslek kurallarını yutar).
 */
function KuralEditorModal({ meslekId, meslekAdi, kaldirildi = false, fuarId, scopeFuarId, fileTypes, onClose, onSaved, uyruk = 0 }) {
  const { t } = useTranslation("yonetim");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Yazılacak kapsam — okunan kapsamla aynı başlar, ama kullanıcı değiştirebilir.
  const [writeScope, setWriteScope] = useState(scopeFuarId);
  const [selectedIds, setSelectedIds] = useState([]);
  const [aciklama, setAciklama] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        // WAS (until #430): getMeslekBelgeKural(meslekId, scopeFuarId)
        const res = await getMeslekBelgeKural(meslekId, scopeFuarId, uyruk);
        if (ignore) return;
        setData(res);
        // Formu, YAZILACAK kapsamda hâlihazırda ne varsa onunla doldur; o kapsamda kural
        // yoksa yürürlükteki (etkin) kuralı başlangıç noktası olarak al.
        const kapsamKurali = writeScope === GLOBAL_SCOPE ? res?.globalKural : res?.fuarKurali;
        const kaynakKural = kapsamKurali ?? res?.etkin;
        setSelectedIds(kaynakKural?.fileTypeIds ?? []);
        setAciklama(kapsamKurali?.aciklama ?? "");
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
    // writeScope değişince formu yeniden doldurmak istemiyoruz (kullanıcı seçim yapmış
    // olabilir) — kapsam değişimi ayrı bir handler'da ele alınıyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meslekId, scopeFuarId]);

  function handleScopeChange(next) {
    setWriteScope(next);
    if (touched) return; // kullanıcının seçimini ezme
    const kapsamKurali = next === GLOBAL_SCOPE ? data?.globalKural : data?.fuarKurali;
    const kaynakKural = kapsamKurali ?? data?.etkin;
    setSelectedIds(kaynakKural?.fileTypeIds ?? []);
    setAciklama(kapsamKurali?.aciklama ?? "");
  }

  function toggleId(id) {
    setTouched(true);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].sort((a, b) => a - b)));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await saveMeslekBelgeKural(meslekId, { fuarId: writeScope, uyruk, fileTypeIds: selectedIds, aciklama });
      const fuarAdi = writeScope === GLOBAL_SCOPE ? t("meslekYonetim.tumFuarlar") : t("meslekYonetim.fuar", { writeScope });
      // #430: the nationality level rides in the scope name of the confirmation
      const kapsamAdi = uyruk === UYRUK_DUZEYI.HEPSI ? fuarAdi : `${fuarAdi}, ${t(uyruk === UYRUK_DUZEYI.YERLI ? "meslekYonetim.uyrukYerli" : "meslekYonetim.uyrukYabanci")}`;
      onSaved(t("meslekYonetim.kuraliKapsamindaKaydedildiBelge", { meslekAdi, kapsamAdi, length: selectedIds.length }));
    } catch (e) {
      setError(staffAwareMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    setError(null);
    try {
      const dusenKural = await deleteMeslekBelgeKural(meslekId, writeScope, uyruk);   // WAS (until #430): (meslekId, writeScope)
      const kaynak = KURAL_KAYNAK[dusenKural?.kaynak]?.label ?? dusenKural?.kaynak ?? t("meslekYonetim.ustKademe");
      onSaved(t("meslekYonetim.kuraliBuKapsamdanSilindiArtik", { meslekAdi, kaynak }));
    } catch (e) {
      setError(e.status === 404 ? t("meslekYonetim.buKapsamdaYazilmisBirKural") : staffAwareMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const kapsamdaKuralVar = Boolean(writeScope === GLOBAL_SCOPE ? data?.globalKural : data?.fuarKurali);
  const fuarKuraliYutuyor =
    writeScope === GLOBAL_SCOPE && meslekId !== TUM_MESLEKLER_ID && Boolean(data?.fuarKurali);

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {meslekId === TUM_MESLEKLER_ID ? t("meslekYonetim.tumMesleklerBelgeKurali") : t("meslekYonetim.belgeKurali", { meslekAdi })}
        {kaldirildi && <Badge color="light" className="text-body border fw-normal ms-2">{t("meslekYonetim.kaldirilmis")}</Badge>}
        {uyruk !== UYRUK_DUZEYI.HEPSI && (
          <Badge color="info" className="fw-normal ms-2" data-testid="uyruk-rozeti">{t(uyruk === UYRUK_DUZEYI.YERLI ? "meslekYonetim.uyrukYerli" : "meslekYonetim.uyrukYabanci")}</Badge>
        )}
      </ModalHeader>
      <ModalBody>
        {loading && <div className="text-center py-4"><Spinner color="primary" /></div>}
        {error && <Alert fade={false} color="danger">{error}</Alert>}

        {!loading && data && (
          <>
            {/* #430: which people this rule is for, and the trap of the "any" level */}
            <Alert fade={false} color="info" className="small py-2" data-testid="uyruk-editor-notu">
              {t(uyruk === UYRUK_DUZEYI.YERLI ? "meslekYonetim.uyrukYerliNotu" : uyruk === UYRUK_DUZEYI.YABANCI ? "meslekYonetim.uyrukYabanciNotu" : "meslekYonetim.uyrukHepsiNotu")}
            </Alert>
            <div className="mb-3 p-3 rounded bg-light">
              <div className="small text-muted mb-1">{t("meslekYonetim.suAndaYururlukteOlanKural")}</div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <KaynakBadge kaynak={data.etkin?.kaynak} />
                <BelgeChips kural={data.etkin} fileTypes={fileTypes} />
              </div>
              {data.etkin?.aciklama && (
                <div className="small text-muted mt-2 fst-italic">"{data.etkin.aciklama}"</div>
              )}
            </div>

            <Row className="g-2 mb-3">
              <Col md={6}>
                <div className="p-2 border rounded h-100">
                  <div className="small fw-semibold mb-1">{t("meslekYonetim.fuarKurali", { fuarId })}</div>
                  {data.fuarKurali
                    ? <BelgeChips kural={data.fuarKurali} fileTypes={fileTypes} />
                    : <span className="text-muted small">{t("meslekYonetim.yazilmamis")}</span>}
                </div>
              </Col>
              <Col md={6}>
                <div className="p-2 border rounded h-100">
                  <div className="small fw-semibold mb-1">{t("meslekYonetim.tumFuarlarGlobalKurali")}</div>
                  {data.globalKural
                    ? <BelgeChips kural={data.globalKural} fileTypes={fileTypes} />
                    : <span className="text-muted small">{t("meslekYonetim.yazilmamis")}</span>}
                </div>
              </Col>
            </Row>

            <hr />

            <Label className="small text-muted mb-1">{t("meslekYonetim.buKuralHangiKapsamaYazilsin")}</Label>
            <Input
              type="select" bsSize="sm" className="mb-2" value={String(writeScope)}
              onChange={(e) => handleScopeChange(Number(e.target.value))}
            >
              <option value={String(GLOBAL_SCOPE)}>{t("meslekYonetim.tumFuarlarGlobalKural")}</option>
              <option value={String(fuarId)}>{t("meslekYonetim.yalnizcaFuar")}{" "}{fuarId}</option>
            </Input>

            {fuarKuraliYutuyor && (
              <Alert fade={false} color="warning" className="py-2 small d-flex gap-2">
                <AlertTriangle size={16} className="flex-shrink-0" />
                <span>
                  {t("meslekYonetim.fuarIcinBuMeslegeZaten", { fuarId })}{" "}<strong>{t("meslekYonetim.etkilemez")}</strong>{" "}{t("meslekYonetim.fuarKuraliGlobalKuraliYener")}
                </span>
              </Alert>
            )}

            <Label className="small text-muted mb-1 mt-2">{t("meslekYonetim.istenecekBelgeler")}</Label>
            <div className="border rounded p-2 mb-2" style={{ maxHeight: 260, overflowY: "auto" }}>
              {fileTypes.length === 0 && <div className="text-muted small">{t("meslekYonetim.belgeTipiKataloguOkunamadi")}</div>}
              {fileTypes.map((ft) => {
                const kvkk = ft.fileTypeId === KVKK_FILE_TYPE_ID;
                return (
                  <div key={ft.fileTypeId} className="form-check">
                    <Input
                      type="checkbox" className="form-check-input"
                      id={`ft-${ft.fileTypeId}`}
                      checked={selectedIds.includes(ft.fileTypeId)}
                      disabled={kvkk}
                      onChange={() => toggleId(ft.fileTypeId)}
                    />
                    <Label className="form-check-label small" for={`ft-${ft.fileTypeId}`}>
                      {ft.fileTypeName}{" "}
                      <span className="text-muted">#{ft.fileTypeId}</span>
                      {kvkk && (
                        <span className="text-muted fst-italic ms-1">
                          {t("meslekYonetim.herkestenIstenirKurallaKaldirilamaz")}
                        </span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>

            {selectedIds.length === 0 && (
              <Alert fade={false} color="danger" className="py-2 small d-flex gap-2">
                <Info size={16} className="flex-shrink-0" />
                <span>
                  {t("meslekYonetim.bosListeKaydetmek")}{" "}<strong>{t("meslekYonetim.buMeslekHicbirKisiselBelge")}</strong>
                  {t("meslekYonetim.demektirVarsayilanaDonmekDegildirVarsayilana")}
                  {" "}<em>{t("meslekYonetim.kuraliSil")}</em>{" "}{t("meslekYonetim.kullanin")}
                </span>
              </Alert>
            )}

            <Label className="small text-muted mb-1">{t("meslekYonetim.aciklamaNeden")}</Label>
            <Input
              type="textarea" rows={2} bsSize="sm" value={aciklama}
              onChange={(e) => { setTouched(true); setAciklama(e.target.value); }}
              placeholder={t("meslekYonetim.ornNakliyeciAractanInmiyorSaha")}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>{t("meslekYonetim.vazgec")}</Button>
        {kapsamdaKuralVar && (
          <Button color="danger" outline onClick={handleDelete} disabled={saving || loading}>
            <CornerLeftUp size={14} className="me-1" />{t("meslekYonetim.kuraliSilUstKademeyeBirak")}
          </Button>
        )}
        <Button color="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? <Spinner size="sm" /> : <><Save size={14} className="me-1" />{t("meslekYonetim.kuraliKaydet")}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * "Yeni Meslek". Ad zaten kullanılıyorsa backend 409 döner (büyük/küçük harf ayırmadan) ve
 * çakışan mesleği verir. Çakışan KALDIRILMIŞSA kopya açmak yerine Geri Al öneriliyor: kopya,
 * kişileri ve kuralı ikiye böler.
 */
function YeniMeslekModal({ onClose, onCreated }) {
  const { t } = useTranslation("yonetim");
  const [ad, setAd] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [kaldirilmisCakisan, setKaldirilmisCakisan] = useState(null); // 409'da kaldırılmış meslek

  async function handleSubmit(e) {
    e.preventDefault();
    if (!ad.trim()) { setError(t("meslekYonetim.meslekAdiZorunludur")); return; }
    setSubmitting(true);
    setError(null);
    setKaldirilmisCakisan(null);
    try {
      await createMeslek({ ad: ad.trim(), aciklama: aciklama.trim() });
      onCreated(t("meslekYonetim.meslegiOlusturuldu", { v: ad.trim() }));
    } catch (err) {
      if (err.status === 409 && err.data?.kaldirildi && err.data?.meslekId != null) {
        setKaldirilmisCakisan(err.data);
      } else {
        setError(staffAwareMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGeriAl() {
    setSubmitting(true);
    setError(null);
    try {
      await geriAlMeslek(kaldirilmisCakisan.meslekId);
      onCreated(t("meslekYonetim.meslegiGeriAlindi", { ad: kaldirilmisCakisan.ad }));
    } catch (err) {
      setError(staffAwareMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>{t("meslekYonetim.yeniMeslek")}</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert fade={false} color="danger" className="d-flex gap-2">
              <X size={18} className="flex-shrink-0 mt-1" />
              <div className="small">{error}</div>
            </Alert>
          )}
          {kaldirilmisCakisan && (
            <Alert fade={false} color="warning" className="d-flex align-items-center gap-2">
              <AlertTriangle size={18} className="flex-shrink-0" />
              <div className="small flex-grow-1">
                <strong>{kaldirilmisCakisan.ad}</strong>{" "}{t("meslekYonetim.adliBirMeslekDahaOnce")}
              </div>
              <Button color="warning" size="sm" type="button" className="text-nowrap flex-shrink-0" disabled={submitting} onClick={handleGeriAl}>
                <ArchiveRestore size={13} className="me-1" />{t("meslekYonetim.geriAl")}
              </Button>
            </Alert>
          )}
          <Label className="small">{t("meslekYonetim.meslekAdi")}</Label>
          <Input value={ad} onChange={(e) => setAd(e.target.value)} placeholder={t("meslekYonetim.ornVincOperatoru")} required />
          <Label className="small mt-3">{t("meslekYonetim.aciklama")}</Label>
          <Input type="textarea" rows={2} value={aciklama} onChange={(e) => setAciklama(e.target.value)} />
          <div className="small text-muted mt-3">
            {t("meslekYonetim.meslekOlusturulduktanSonraBelgeKuralini")}{" "}<em>{t("meslekYonetim.kuraliDuzenle")}</em>{" "}{t("meslekYonetim.ileYazabilirsinizKuralYazilmazsaVarsayilan")}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose}>{t("common:kapat")}</Button>
          <Button color="success" type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : t("meslekYonetim.olustur")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

/**
 * Staff reddini insan diline çevirir. Backend bu reddi yedi uçta 403, iki uçta 400 ile
 * verdiği için (bkz. kisiAdminService.isStaffRefusal) mesaj tek yerden üretiliyor.
 */
function staffAwareMessage(error) {
  if (isStaffRefusal(error)) {
    return i18n.t("yonetim:meslekYonetim.buIslemSakaPersoneliYetkisi") +
      i18n.t("yonetim:meslekYonetim.firmaOlarakGorunuyorGrupSatiri");
  }
  return error.message;
}
